import { z } from 'zod';
import { commaSeparated } from '../common/comma-separated';
import { PhoneNumberSchema } from '../common/phone-number.schema';
import { LegalBasisSchema } from '../common/legal-basis.schema';
import { IdsFilterQuerySchema } from '../common/ids-filter.schema';
import { ListSearchTermSchema } from '../common/search-term.schema';
import { MANUAL_COMMUNICATION_TYPES } from '../common/communication-vocabulary.schema';
import { refersTo } from '../common/refers-to';

// Domain enum tokens are en-US per ADR 0009 (2026-05-21 amendment). The
// German labels users see live in `packages/i18n`; adding a value here
// forces a translation update in every locale.

export const CONTACT_TYPES_DISCRIMINATOR = ['PERSON', 'COMPANY'] as const;
export const ContactTypeDiscriminatorSchema = z.enum(CONTACT_TYPES_DISCRIMINATOR);
export type ContactTypeDiscriminator = z.infer<typeof ContactTypeDiscriminatorSchema>;

export const CONTACT_SOURCES = [
  'MANUAL',
  'PARTNER',
  'PORTAL',
  'WEBSITE',
  'REFERRAL',
  'COLD_OUTREACH',
  'OTHER',
] as const;
export const ContactSourceSchema = z.enum(CONTACT_SOURCES);
export type ContactSource = z.infer<typeof ContactSourceSchema>;

export const CONTACT_TYPES = ['OWNER', 'BUYER', 'TENANT', 'LANDLORD', 'PARTNER'] as const;
export const ContactTypeSchema = z.enum(CONTACT_TYPES);
export type ContactType = z.infer<typeof ContactTypeSchema>;

export const SALUTATIONS = ['MR', 'MRS', 'MX', 'NONE'] as const;
export const SalutationSchema = z.enum(SALUTATIONS);
export type Salutation = z.infer<typeof SalutationSchema>;

export const CONTACT_PHONE_TYPES = ['MOBILE', 'OFFICE', 'LANDLINE', 'OTHER'] as const;
export const ContactPhoneTypeSchema = z.enum(CONTACT_PHONE_TYPES);
export type ContactPhoneType = z.infer<typeof ContactPhoneTypeSchema>;

export const CONTACT_PHONE_MESSAGING = ['WHATSAPP', 'SMS', 'NONE'] as const;
export const ContactPhoneMessagingSchema = z.enum(CONTACT_PHONE_MESSAGING);
export type ContactPhoneMessaging = z.infer<typeof ContactPhoneMessagingSchema>;

export const ContactAddressSchema = z.object({
  street: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string(),
});
export type ContactAddress = z.infer<typeof ContactAddressSchema>;

export const CONTACT_PERSON_DETAIL_MAX_LENGTH = 80;

const ContactPersonBodySchema = z.object({
  salutation: SalutationSchema.nullable().default(null),
  firstName: z.string().trim().min(1).max(120),
  lastName: z.string().trim().min(1).max(120),
  dateOfBirth: z.iso.date().nullable().default(null),
  // Absent keeps the stored value on a profile update and means none on create;
  // null clears it (BL-137: the profile PUT replaces the whole block, and callers
  // that never held these two must not wipe them).
  birthName: z.string().trim().min(1).max(CONTACT_PERSON_DETAIL_MAX_LENGTH).nullable().optional(),
  birthPlace: z.string().trim().min(1).max(CONTACT_PERSON_DETAIL_MAX_LENGTH).nullable().optional(),
  nationality: z.string().trim().min(1).max(CONTACT_PERSON_DETAIL_MAX_LENGTH).nullable().default(null),
  language: z.string().trim().min(1).max(80).nullable().default(null),
});

const ContactCompanyBodySchema = z.object({
  companyName: z.string().trim().min(1).max(200),
});

export const ContactEmailAddressSchema = z.email();

const ContactEmailBodySchema = z.object({
  email: ContactEmailAddressSchema,
  isPrimary: z.boolean().default(false),
});

const ContactPhoneBodySchema = z.object({
  phone: PhoneNumberSchema,
  type: ContactPhoneTypeSchema,
  messaging: ContactPhoneMessagingSchema.default('NONE'),
  isPrimary: z.boolean().default(false),
});

const ContactAddressBodySchema = z.object({
  street: z.string().trim().min(1).max(120).nullable().default(null),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/)
    .nullable()
    .default(null),
  city: z.string().trim().min(1).max(80).nullable().default(null),
  country: z.string().trim().min(1).max(80).default('Deutschland'),
});

export const CreateContactBodySchema = z
  .object({
    contactType: ContactTypeDiscriminatorSchema,
    source: ContactSourceSchema.default('MANUAL'),
    // GDPR processing basis (IMM-7). Optional on manual create; carried over
    // unchanged when a qualified lead converts into a contact (IMM-193).
    legalBasis: LegalBasisSchema.nullable().default(null),
    warningNote: z.string().trim().min(1).nullable().default(null),
    person: ContactPersonBodySchema.nullable().default(null),
    company: ContactCompanyBodySchema.nullable().default(null),
    types: z.array(ContactTypeSchema).default([]),
    emails: z.array(ContactEmailBodySchema).default([]),
    phones: z.array(ContactPhoneBodySchema).default([]),
    address: ContactAddressBodySchema.nullable().default(null),
  })
  .refine(
    (value) =>
      value.contactType === 'PERSON'
        ? value.person !== null && value.company === null
        : value.company !== null && value.person === null,
    { message: 'contactType must match the populated profile block (person for PERSON, company for COMPANY)' },
  );

export type CreateContactBody = z.infer<typeof CreateContactBodySchema>;

export const CreateContactPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

const ContactPersonResponseSchema = z.object({
  salutation: SalutationSchema.nullable(),
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.iso.date().nullable(),
  birthName: z.string().nullable(),
  birthPlace: z.string().nullable(),
  nationality: z.string().nullable(),
  language: z.string().nullable(),
});

const ContactCompanyResponseSchema = z.object({
  companyName: z.string(),
});

const ContactEmailResponseSchema = z.object({
  id: z.uuid(),
  email: z.email(),
  isPrimary: z.boolean(),
  createdAt: z.iso.datetime(),
});

const ContactPhoneResponseSchema = z.object({
  id: z.uuid(),
  phone: z.string(),
  type: ContactPhoneTypeSchema,
  messaging: ContactPhoneMessagingSchema,
  isPrimary: z.boolean(),
  createdAt: z.iso.datetime(),
});

export const CONTACT_NOTE_MAX_LENGTH = 10_000;

const ContactNoteResponseSchema = z.object({
  id: z.uuid(),
  body: z.string(),
  createdByActorId: refersTo('member', z.uuid()).nullable(),
  occurredAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});
export type ContactNoteResponse = z.infer<typeof ContactNoteResponseSchema>;

export const ContactResponseSchema = z.object({
  id: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: refersTo('organization', z.uuid()),
  assignedToMemberId: refersTo('member', z.uuid()).nullable(),
  contactType: ContactTypeDiscriminatorSchema,
  displayName: z.string(),
  source: ContactSourceSchema,
  legalBasis: LegalBasisSchema.nullable(),
  warningNote: z.string().nullable(),
  person: ContactPersonResponseSchema.nullable(),
  company: ContactCompanyResponseSchema.nullable(),
  types: z.array(ContactTypeSchema),
  emails: z.array(ContactEmailResponseSchema),
  phones: z.array(ContactPhoneResponseSchema),
  address: ContactAddressSchema.nullable(),
  notes: z.array(ContactNoteResponseSchema),
  lastContactedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  // Optimistic-lock token (IMM-1046.1) — system-assigned, never accepted from
  // a caller. Bumped on every write of the row; never on create.
  version: z.number().int().positive(),
});

export type ContactResponse = z.infer<typeof ContactResponseSchema>;

export const CreateContactResponseSchema = ContactResponseSchema;
export type CreateContactResponse = z.infer<typeof CreateContactResponseSchema>;

/**
 * Slim list-row shape. Server-derives `primaryEmail` and `primaryPhone`
 * from the child collections (first row flagged `isPrimary`, else first
 * by `createdAt`).
 */
export const ContactListItemSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  contactType: ContactTypeDiscriminatorSchema,
  primaryEmail: z.email().nullable(),
  primaryPhone: z.string().nullable(),
  city: z.string().nullable(),
  updatedAt: z.iso.datetime(),
});

export type ContactListItem = z.infer<typeof ContactListItemSchema>;

export const ListWorkspaceContactsPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

export const ListWorkspaceContactsResponseSchema = z.object({
  data: z.array(ContactListItemSchema),
});

export type ListWorkspaceContactsResponse = z.infer<typeof ListWorkspaceContactsResponseSchema>;

// Optional free-text search applied as a single combined match across name
// (display name), e-mail, and phone. Phone matching normalises both the query
// and the stored values to E.164 with the German default country code `+49`, so
// "0151…" and "+49 151…" both hit. Absent or blank (`?q=`) → no search; the full
// (scope-restricted) list is returned unchanged.
// IMM-448.4 promoted the shape to `ListSearchTermSchema` so Objekte, Deals and
// Leads share it verbatim; this alias keeps the Kontakte-flavoured name (and its
// matching semantics above) at the call sites that already read it.
export const ContactSearchTermSchema = ListSearchTermSchema;

// Org-addressed list query: the shared `?ids=` batch filter plus the optional
// `?q=` search term.
export const ListWorkspaceContactsQuerySchema = IdsFilterQuerySchema.extend({
  q: ContactSearchTermSchema,
});

export type ListWorkspaceContactsQuery = z.infer<typeof ListWorkspaceContactsQuerySchema>;

// Workspace-addressed visible-Standort list (IMM-20.4). Same row shape as the
// org-addressed list, but scoped to the caller's visible org set (their
// affiliated Standorte + descendants) OR records assigned to the caller. The
// optional `?organizationId=` narrows the result to a single Standort within
// that visible set; `?ids=` batch-restricts as on the org-addressed list.
export const ListVisibleContactsPathParamsSchema = z.object({
  workspaceId: z.uuid(),
});

export const ListVisibleContactsQuerySchema = IdsFilterQuerySchema.extend({
  q: ContactSearchTermSchema,
  organizationId: z.uuid().optional(),
  // Narrow to this set of Standorte (a node + its descendants), intersected
  // with the caller's visible set. The frontend computes the subtree id set
  // from the org tree it holds and sends it `?organizationIds=uuid,uuid`. Takes
  // precedence over the single `organizationId` when non-empty; ids outside the
  // visible set are ignored (never widens). Absent / blank → no narrowing.
  organizationIds: commaSeparated(z.uuid()),
  // Exact, case-insensitive match against ANY of the Kontakt's e-mail addresses
  // (IMM-1177.4). AND-combined with the visible scope and `q`; never widens.
  email: z.email().optional(),
});

export const ListVisibleContactsResponseSchema = ListWorkspaceContactsResponseSchema;
export type ListVisibleContactsResponse = z.infer<typeof ListVisibleContactsResponseSchema>;

export const GetWorkspaceContactPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const GetWorkspaceContactResponseSchema = ContactResponseSchema;
export type GetWorkspaceContactResponse = z.infer<typeof GetWorkspaceContactResponseSchema>;

export const GetVisibleContactPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});

// The workspace-addressed detail read adds where to address the contact's
// organisation-scoped READS (IMM-1105): its own Standort when the caller sees
// it, otherwise the Standort of the Deal, Lead or Objekt that links the caller
// to it. A link opens the contact read-only — when this differs from
// `organizationId`, every write answers as for a contact the caller cannot see.
export const GetVisibleContactResponseSchema = ContactResponseSchema.extend({
  accessOrganizationId: refersTo('organization', z.uuid()),
});
export type GetVisibleContactResponse = z.infer<typeof GetVisibleContactResponseSchema>;

const UpdateContactPathParamsSchema = z.object({
  organizationId: refersTo('organization', z.uuid()),
  id: z.uuid(),
});

export const UpdateContactProfileBodySchema = z
  .object({
    contactType: ContactTypeDiscriminatorSchema,
    person: ContactPersonBodySchema.nullable().default(null),
    company: ContactCompanyBodySchema.nullable().default(null),
  })
  .refine(
    (value) =>
      value.contactType === 'PERSON'
        ? value.person !== null && value.company === null
        : value.company !== null && value.person === null,
    { message: 'contactType must match the populated profile block (person for PERSON, company for COMPANY)' },
  );

export type UpdateContactProfileBody = z.infer<typeof UpdateContactProfileBodySchema>;
export const UpdateContactProfilePathParamsSchema = UpdateContactPathParamsSchema;
export const UpdateContactProfileResponseSchema = ContactResponseSchema;
export type UpdateContactProfileResponse = z.infer<typeof UpdateContactProfileResponseSchema>;

export const UpdateContactTypesBodySchema = z.object({
  types: z.array(ContactTypeSchema).default([]),
});
export type UpdateContactTypesBody = z.infer<typeof UpdateContactTypesBodySchema>;
export const UpdateContactTypesPathParamsSchema = UpdateContactPathParamsSchema;
export const UpdateContactTypesResponseSchema = ContactResponseSchema;
export type UpdateContactTypesResponse = z.infer<typeof UpdateContactTypesResponseSchema>;

export const UpdateContactEmailsBodySchema = z.object({
  emails: z.array(ContactEmailBodySchema).default([]),
});
export type UpdateContactEmailsBody = z.infer<typeof UpdateContactEmailsBodySchema>;
export const UpdateContactEmailsPathParamsSchema = UpdateContactPathParamsSchema;
export const UpdateContactEmailsResponseSchema = ContactResponseSchema;
export type UpdateContactEmailsResponse = z.infer<typeof UpdateContactEmailsResponseSchema>;

export const UpdateContactPhonesBodySchema = z.object({
  phones: z.array(ContactPhoneBodySchema).default([]),
});
export type UpdateContactPhonesBody = z.infer<typeof UpdateContactPhonesBodySchema>;
export const UpdateContactPhonesPathParamsSchema = UpdateContactPathParamsSchema;
export const UpdateContactPhonesResponseSchema = ContactResponseSchema;
export type UpdateContactPhonesResponse = z.infer<typeof UpdateContactPhonesResponseSchema>;

export const UpdateContactAddressBodySchema = z.object({
  address: ContactAddressBodySchema.nullable().default(null),
});
export type UpdateContactAddressBody = z.infer<typeof UpdateContactAddressBodySchema>;
export const UpdateContactAddressPathParamsSchema = UpdateContactPathParamsSchema;
export const UpdateContactAddressResponseSchema = ContactResponseSchema;
export type UpdateContactAddressResponse = z.infer<typeof UpdateContactAddressResponseSchema>;

export const UpdateContactWarningNoteBodySchema = z.object({
  warningNote: z.string().trim().min(1).nullable().default(null),
});
export type UpdateContactWarningNoteBody = z.infer<typeof UpdateContactWarningNoteBodySchema>;
export const UpdateContactWarningNotePathParamsSchema = UpdateContactPathParamsSchema;
export const UpdateContactWarningNoteResponseSchema = ContactResponseSchema;
export type UpdateContactWarningNoteResponse = z.infer<typeof UpdateContactWarningNoteResponseSchema>;

// Record a note (Notiz) against a contact. `occurredAt` defaults to now
// server-side; the future-date guard (≤ now, 1-minute skew) lives in the
// domain. The note is the source of truth — the activity timeline projects it
// from the emitted `relationship.contact-note-recorded` event.
export const RecordContactNoteBodySchema = z.object({
  body: z.string().min(1).max(CONTACT_NOTE_MAX_LENGTH),
  occurredAt: z.iso.datetime().optional(),
  propertyId: z.uuid().optional(),
  dealId: z.uuid().optional(),
});
export type RecordContactNoteBody = z.infer<typeof RecordContactNoteBodySchema>;
export const RecordContactNotePathParamsSchema = UpdateContactPathParamsSchema;
export const RecordContactNoteResponseSchema = ContactNoteResponseSchema;
export type RecordContactNoteResponse = z.infer<typeof RecordContactNoteResponseSchema>;

// A communication is a contact reaching out or being reached — distinct from a
// Note (an internal observation). Each carries a direction; Fax/Letter/E-Mail
// also a subject, Call also a duration. The timeline projects these alongside
// notes.
export const CONTACT_COMMUNICATION_TYPES = MANUAL_COMMUNICATION_TYPES;
export const ContactCommunicationTypeSchema = z.enum(CONTACT_COMMUNICATION_TYPES);
export type ContactCommunicationType = z.infer<typeof ContactCommunicationTypeSchema>;

export const CONTACT_COMMUNICATION_DIRECTIONS = ['INBOUND', 'OUTBOUND'] as const;
export const ContactCommunicationDirectionSchema = z.enum(CONTACT_COMMUNICATION_DIRECTIONS);
export type ContactCommunicationDirection = z.infer<typeof ContactCommunicationDirectionSchema>;

export const CONTACT_COMMUNICATION_CONTENT_MAX_LENGTH = 10_000;
export const CONTACT_COMMUNICATION_SUBJECT_MAX_LENGTH = 200;
export const CONTACT_COMMUNICATION_DURATION_MAX_MINUTES = 1_440;

const ContactCommunicationResponseSchema = z.object({
  id: z.uuid(),
  type: ContactCommunicationTypeSchema,
  direction: ContactCommunicationDirectionSchema,
  content: z.string(),
  subject: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
  createdByActorId: refersTo('member', z.uuid()).nullable(),
  occurredAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});
export type ContactCommunicationResponse = z.infer<typeof ContactCommunicationResponseSchema>;

// `subject` is only meaningful for FAX / LETTER / EMAIL and `durationMinutes`
// only for CALL; the domain ignores them for other types. `occurredAt`
// defaults to now; the future-date guard (≤ now, 1-minute skew) lives in the
// domain.
export const RecordContactCommunicationBodySchema = z.object({
  type: ContactCommunicationTypeSchema,
  direction: ContactCommunicationDirectionSchema,
  content: z.string().min(1).max(CONTACT_COMMUNICATION_CONTENT_MAX_LENGTH),
  subject: z.string().max(CONTACT_COMMUNICATION_SUBJECT_MAX_LENGTH).optional(),
  durationMinutes: z.number().int().min(0).max(CONTACT_COMMUNICATION_DURATION_MAX_MINUTES).optional(),
  occurredAt: z.iso.datetime().optional(),
  propertyId: z.uuid().optional(),
  dealId: z.uuid().optional(),
});
export type RecordContactCommunicationBody = z.infer<typeof RecordContactCommunicationBodySchema>;
export const RecordContactCommunicationPathParamsSchema = UpdateContactPathParamsSchema;
export const RecordContactCommunicationResponseSchema = ContactCommunicationResponseSchema;
export type RecordContactCommunicationResponse = z.infer<typeof RecordContactCommunicationResponseSchema>;

// Amend a recorded note / communication. Same validation as recording; the
// type is immutable (a note stays a note), so the body mirrors the record body.
// Publishes `relationship.contact-{note,communication}-updated`, which the
// activity timeline projects as a new content version ("bearbeitet").
export const AmendContactNoteBodySchema = z.object({
  body: z.string().min(1).max(CONTACT_NOTE_MAX_LENGTH),
  occurredAt: z.iso.datetime().optional(),
});
export type AmendContactNoteBody = z.infer<typeof AmendContactNoteBodySchema>;
export const AmendContactNotePathParamsSchema = UpdateContactPathParamsSchema.extend({ noteId: z.uuid() });
export const AmendContactNoteResponseSchema = ContactNoteResponseSchema;
export type AmendContactNoteResponse = z.infer<typeof AmendContactNoteResponseSchema>;

export const AmendContactCommunicationBodySchema = z.object({
  direction: ContactCommunicationDirectionSchema,
  content: z.string().min(1).max(CONTACT_COMMUNICATION_CONTENT_MAX_LENGTH),
  subject: z.string().max(CONTACT_COMMUNICATION_SUBJECT_MAX_LENGTH).optional(),
  durationMinutes: z.number().int().min(0).max(CONTACT_COMMUNICATION_DURATION_MAX_MINUTES).optional(),
  occurredAt: z.iso.datetime().optional(),
});
export type AmendContactCommunicationBody = z.infer<typeof AmendContactCommunicationBodySchema>;
export const AmendContactCommunicationPathParamsSchema = UpdateContactPathParamsSchema.extend({
  communicationId: z.uuid(),
});
export const AmendContactCommunicationResponseSchema = ContactCommunicationResponseSchema;
export type AmendContactCommunicationResponse = z.infer<typeof AmendContactCommunicationResponseSchema>;

export const DeleteContactPathParamsSchema = UpdateContactPathParamsSchema;
export const DeleteContactResponseSchema = z.object({ id: z.uuid() });
export type DeleteContactResponse = z.infer<typeof DeleteContactResponseSchema>;

export const DeleteContactNotePathParamsSchema = UpdateContactPathParamsSchema.extend({ noteId: z.uuid() });
export const DeleteContactNoteResponseSchema = z.object({ id: z.uuid() });
export type DeleteContactNoteResponse = z.infer<typeof DeleteContactNoteResponseSchema>;

export const DeleteContactCommunicationPathParamsSchema = UpdateContactPathParamsSchema.extend({
  communicationId: z.uuid(),
});
export const DeleteContactCommunicationResponseSchema = z.object({ id: z.uuid() });
export type DeleteContactCommunicationResponse = z.infer<typeof DeleteContactCommunicationResponseSchema>;

// Cross-Standort reassignment (IMM-20.4 AC22). Workspace-addressed:
// `POST /v1/workspaces/:workspaceId/contacts/:id/standort`. The body carries the
// target organisation; the handler's authz rule decides whether the move is allowed.
export const ReassignContactStandortPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});
export type ReassignContactStandortPathParams = z.infer<typeof ReassignContactStandortPathParamsSchema>;

export const ReassignContactStandortBodySchema = z
  .object({
    organizationId: z.uuid(),
  })
  .strict();
export type ReassignContactStandortBody = z.infer<typeof ReassignContactStandortBodySchema>;

export const ReassignContactStandortResponseSchema = ContactResponseSchema;
export type ReassignContactStandortResponse = z.infer<typeof ReassignContactStandortResponseSchema>;
